/**
 * LANGUAGE-CHANGE EVENTS — a leaf, so neither side has to import the other.
 *
 * `i18n.js` is loaded by all three pages. `socket-handler.js` is loaded only by
 * the game page. i18n used to reach the socket through a global probe
 * (`if (window.SocketHandler && ...)`), which did nothing on landing and
 * storybook; as a static import that optional edge became mandatory and pulled
 * the whole game page into both other bundles.
 *
 * Reversing the arrow — socket-handler imports i18n — fixes the bundles but has
 * its own cost: importing `i18n.js` EXECUTES it, and it assigns `window.i18n`.
 * Three suites that stub `window.i18n` before loading socket-handler had their
 * stub silently overwritten, and asserted on English strings they had replaced.
 *
 * So the registry lives here instead, in a module with no imports at all.
 * `i18n.js` emits, `socket-handler.js` subscribes, and neither appears in the
 * other's dependency graph.
 */

export type LanguageListener = (language: string) => void;

const listeners = new Set<LanguageListener>();

/**
 * Register a callback invoked with the language code whenever it changes.
 * Returns an unsubscribe function.
 */
export function onLanguageChange(listener: LanguageListener): () => void {
    if (typeof listener !== 'function') return () => {};
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}

/**
 * Notify every subscriber. One listener throwing must not stop the others, and
 * must never take down the language switch that triggered it.
 */
export function emitLanguageChange(language: string): void {
    for (const listener of listeners) {
        try {
            listener(language);
        } catch (error) {
            console.error('[i18n] language listener failed:', error);
        }
    }
}
